import { useState } from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "../components/marketing/ScrollReveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../contexts/LanguageContext";
import PageHead from "../components/PageHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", societe: "", profil: "", sujet: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.email || !form.societe || !form.profil) {
      toast.error(t("contact.errRequired"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSent(true);
      toast.success(t("contact.okSend"));
    } catch {
      toast.error(t("contact.errSend"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHead title="Contact" description="Contactez KYRA pour une demonstration. Enseignes, distributeurs, investisseurs." />
      <div style={{ paddingTop: 60, background: "var(--kyra-bg)" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start max-w-[1200px] mx-auto" style={{ padding: "96px 5.5%" }}>
          {/* LEFT */}
          <div>
            <ScrollReveal>
              <div className="flex gap-1.5 flex-wrap mb-6">
                <span className="text-[10px] font-medium uppercase tracking-[3px] px-2.5 py-1 rounded-sm" style={{ border: "1px solid rgba(27,67,50,0.25)", color: "var(--kyra-gr)" }}>{t("contact.kw1")}</span>
                <span className="text-[10px] font-medium uppercase tracking-[3px] px-2.5 py-1 rounded-sm" style={{ border: "1px solid rgba(176,48,37,0.25)", color: "var(--kyra-rd)" }}>{t("contact.kw2")}</span>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <h2 className="font-black leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(30px,4vw,48px)", letterSpacing: -1.5 }}>
                {t("contact.h2_1")}<br /><em style={{ color: "var(--kyra-gr)" }}>{t("contact.h2_2")}</em>
              </h2>
            </ScrollReveal>
            <ScrollReveal delay="d1">
              <p className="text-[15px] font-light leading-[1.78] mb-10" style={{ color: "var(--kyra-mu)" }}>
                {t("contact.sub")}
              </p>
            </ScrollReveal>
            <ScrollReveal delay="d2">
              <div className="flex flex-col gap-6 mb-10">
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] tracking-[3px] uppercase font-medium" style={{ color: "var(--kyra-mu)" }}>Email</span>
                  <span className="text-sm"><a href="mailto:contact@kyra.fr" className="no-underline pb-px" style={{ color: "var(--kyra-gr)", borderBottom: "1px solid rgba(27,67,50,0.2)" }}>contact@kyra.fr</a></span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] tracking-[3px] uppercase font-medium" style={{ color: "var(--kyra-mu)" }}>Societe</span>
                  <span className="text-sm">KYRA — France</span>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay="d2">
              <p className="text-[11px] tracking-[3px] uppercase font-medium mb-3" style={{ color: "var(--kyra-mu)" }}>Vous etes</p>
              <div className="flex flex-col" style={{ border: "1px solid var(--kyra-li)" }}>
                {[
                  { dot: "var(--kyra-gr)", text: "Retailer ou enseigne GMS — vous gerez un programme de fidelite" },
                  { dot: "var(--kyra-rd)", text: "Distributeur ou centrale d'achat — vous pilotez la supply chain" },
                  { dot: "var(--kyra-am)", text: "Investisseur ou partenaire strategique — vous cherchez a soutenir un impact reel" },
                ].map((p, i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: i < 2 ? "1px solid var(--kyra-li)" : "none" }}>
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.dot }} />
                    <span className="text-[13px] font-light" style={{ color: "var(--kyra-mu)" }}>{p.text}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>

          {/* FORM */}
          <ScrollReveal delay="d1">
            <div className="bg-white border rounded p-8 md:p-10" style={{ borderColor: "var(--kyra-li)", boxShadow: "0 2px 24px rgba(0,0,0,0.04)" }}>
              {!sent ? (
                <>
                  <h3 className="text-xl font-black tracking-tight mb-1.5" style={{ fontFamily: "'Playfair Display'" }}>{t("contact.formTitle")}</h3>
                  <p className="text-[13px] font-light mb-7 pb-6" style={{ color: "var(--kyra-mu)", borderBottom: "1px solid var(--kyra-li)" }}>{t("contact.formSub")}</p>
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                      <div>
                        <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Prenom *</Label>
                        <Input data-testid="contact-prenom" placeholder="Jean" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" required />
                      </div>
                      <div>
                        <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Nom *</Label>
                        <Input data-testid="contact-nom" placeholder="Dupont" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" required />
                      </div>
                    </div>
                    <div className="mb-5">
                      <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Email professionnel *</Label>
                      <Input data-testid="contact-email" type="email" placeholder="j.dupont@enseigne.fr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" required />
                    </div>
                    <div className="mb-5">
                      <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Societe *</Label>
                      <Input data-testid="contact-societe" placeholder="Nom de votre enseigne ou groupe" value={form.societe} onChange={(e) => setForm({ ...form, societe: e.target.value })} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" required />
                    </div>
                    <div className="mb-5">
                      <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Vous etes *</Label>
                      <Select value={form.profil} onValueChange={(v) => setForm({ ...form, profil: v })}>
                        <SelectTrigger data-testid="contact-profil" className="bg-[var(--kyra-bg)] border-[var(--kyra-li)]">
                          <SelectValue placeholder="Selectionner votre profil" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="retailer">Retailer / Enseigne GMS</SelectItem>
                          <SelectItem value="distributeur">Distributeur / Centrale d'achat</SelectItem>
                          <SelectItem value="investisseur">Investisseur / VC</SelectItem>
                          <SelectItem value="partenaire">Partenaire strategique</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="mb-5">
                      <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Sujet</Label>
                      <Select value={form.sujet} onValueChange={(v) => setForm({ ...form, sujet: v })}>
                        <SelectTrigger data-testid="contact-sujet" className="bg-[var(--kyra-bg)] border-[var(--kyra-li)]">
                          <SelectValue placeholder="Selectionner un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="demo">Demander une demonstration</SelectItem>
                          <SelectItem value="rappels">Module Rappels produits</SelectItem>
                          <SelectItem value="dlc">Module Notifications DLC</SelectItem>
                          <SelectItem value="partenariat">Partenariat / Investissement</SelectItem>
                          <SelectItem value="autre">Autre demande</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="mb-5">
                      <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Message</Label>
                      <Textarea data-testid="contact-message" placeholder="Decrivez votre contexte, vos enjeux..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white min-h-[120px]" />
                    </div>
                    <button data-testid="contact-submit" type="submit" disabled={sending} className="w-full text-white text-sm font-medium py-3.5 rounded-sm transition-colors cursor-pointer disabled:opacity-50" style={{ background: "var(--kyra-gr)", border: "none" }}>
                      {sending ? t("contact.submitting") : t("contact.submitBtn")}
                    </button>
                    <p className="text-[11px] font-light mt-3.5 text-center leading-snug" style={{ color: "var(--kyra-mu)" }}>
                      Vos donnees ne sont utilisees qu'a des fins de reponse a votre demande. <Link to="/engagements" className="underline" style={{ color: "var(--kyra-mu)" }}>Voir nos engagements</Link>
                    </p>
                  </form>
                </>
              ) : (
                <div data-testid="contact-success" className="text-center py-10">
                  <h3 className="text-2xl font-black mb-3" style={{ fontFamily: "'Playfair Display'", color: "var(--kyra-gr)" }}>{t("contact.successTitle")}</h3>
                  <p className="text-sm font-light leading-relaxed" style={{ color: "var(--kyra-mu)" }}>
                    {t("contact.successText")}<br />{" "}
                    <Link to="/solutions" style={{ color: "var(--kyra-gr)" }}>{t("contact.successLink")}</Link>
                  </p>
                </div>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}
